'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { pageview, setUserId, trackPageTime } from '@/lib/ga'
import { getUserId } from '@/lib/auth'

/**
 * AnalyticsProvider Component
 * 
 * 
 * This component automatically tracks page views when routes change in Next.js App Router.
 * 
 * Why this is needed -
 * - Next.js App Router uses client-side navigation (SPA behavior)
 * - GA4's automatic page view tracking only works on full page reloads
 * - Without this provider, navigating from /dashboard to /courses won't be tracked
 * - This ensures every route change sends a page view to GA4
 * 
 * How it works:
 * - Uses Next.js hooks (usePathname, useSearchParams) to detect route changes
 * - Calls pageview() helper on each route change
 * - Handles both pathname and search params changes
 * - Formats URL as /path?query=value for GA4
 * - Sets user_id on every page load/route change if user is logged in
 * 
 * Note: This component doesn't render any UI - it only handles analytics tracking.
 */
export default function AnalyticsProvider() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const pageStartTimeRef = useRef<number>(Date.now())
  const previousPathRef = useRef<string>('')

  // Set user_id on mount (before any events fire)
  useEffect(() => {
    // Set user_id immediately if gtag is ready
    const userId = getUserId()
    if (userId) {
      setUserId(userId)
    } else {
      setUserId(null)
    }
  }, []) // Run once on mount

  useEffect(() => {
    // Build the full URL including search params
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')

    // Track time spent on previous page before navigating
    const previousUrl = previousPathRef.current
    if (previousUrl && previousUrl !== url) {
      const timeSpent = Math.floor((Date.now() - pageStartTimeRef.current) / 1000)
      if (timeSpent > 0) {
        trackPageTime(previousUrl, timeSpent)
      }
    }

    // Update refs for new page
    previousPathRef.current = url
    pageStartTimeRef.current = Date.now()

    // Set user_id again on route change (before pageview)
    const userId = getUserId()
    if (userId) {
      setUserId(userId)
    }

    // Track page view on route change (user_id is already set via setUserId and included in pageview)
    pageview(url)

    // Track time when user leaves the page (unload/browser close)
    const handleBeforeUnload = () => {
      const timeSpent = Math.floor((Date.now() - pageStartTimeRef.current) / 1000)
      if (timeSpent > 0) {
        trackPageTime(url, timeSpent)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Note: Previous page time is already tracked at the start of this effect (lines above)
      // This cleanup only handles edge case of component unmount without navigation
      // In Next.js App Router, this is rare but we handle it for completeness
      const timeSpent = Math.floor((Date.now() - pageStartTimeRef.current) / 1000)
      if (timeSpent > 0 && url === previousPathRef.current) {
        trackPageTime(url, timeSpent)
      }
    }
  }, [pathname, searchParams])

  // This component doesn't render anything
  return null
}
